<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string, mixed } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DEmployeesFilter,
  DLoading,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  convertArrayToString,
  getPayroll,
  getProcessCountry,
  getConcepts,
  getReportRequest,
  getPaymentBases
} from '@/app/core/services/reports'
import {
  getClosedPayrolls,
  getBankId,
  getEmployees,
  getCurrenciesId,
  getBankParentId
} from '@/app/core/services/reports/banks'
import {
  ReportBanamexD,
  generateBanamexD,
  getEmployeesByAccountType,
  getPaymentMethodByBank,
  getTerms,
  getLoansTypes,
  getLoans,
  getEmployeesLoans
} from '@/app/core/services/reports/banks/banamex-d'
usePayrollChange(() => back())
/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

let paymentType = '001'

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

const isRequiredField = computed(() => {
  if (banks.value) {
    if ( banks.value && banks.value.length === 1 && banks.value[0] === defaultBank.value ) {
      paymentType = '001'
      term.value = '00'
      return false;
    }
    paymentType = '002'
    return true;
  }
})

const reportTypeValidation = computed(() => {
  if (reportType.value) {
    if (reportType.value === "2") {
      paymentBases.value = itemsPaymentBases.value.map((_: any) => _.id)
      return true;
    }
    return false;
  }
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsConcepts = ref<any[]>([])
const allConcepts = ref<any[]>([])
const allIdsConceptsSelected = ref<any[]>([])
const itemsBanks = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const copyItemsEmployees = ref<string[]>([])
const itemsProcesses = ref<string[]>([])
const itemsCurrencies = ref<any[]>([])
const itemsPaymentBases = ref<any[]>([])
const itemsTerms = ref<any[]>([])
const itemsPaymentMethod = ref<string[]>([])
const searchProcesses = ref<string>('')
const searchConcept = ref<string>('')
const searchCurrencies = ref<string>('')
const searchPaymentMethod = ref<string>('')
const urlFile = ref(null)
const refresh = ref()
const beforeBanks = ref<any[]>([])
const path = route.params.code as string
const decodeCode = atob(path)

const defaultBank = ref<string>('')
const itemsReportTypes = ref([
  { id:  '1', name: 'Pago a Nóminas' },
  { id:  '2', name: 'Pago a Pensión' }
])
const itemsLoansTypes = ref<any[]>([]);
const employeesAgain = ref<any>('')

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  process: string().required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  paymentDate: date().required(t('form.valid.required')),
  concepts: string().required(t('form.valid.required')),
  banks: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  paymentBases: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  currency: string().required(t('form.valid.required')),
  paymentMethod: string().required(t('form.valid.fieldRequired')),
  paymentReference: mixed().when('banks', (tipo: any, schema) => {
    if ( paymentType == '001' ) {
      return string()
        .matches(/^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/, {
          message: t('form.valid.alphanumeric')
        })
        .max(16, t('form.valid.maxLength', [16]))
        .required(t('form.valid.required'))
    } else if ( !(paymentType == '001') ) {
      return string()
        .matches(/^(?!0$)[0-9]+$/, {
          message: t('form.valid.num')
        })
        .max(7, t('form.valid.maxLength', [7]))
    }
    return schema
  }),
  secuence: string()
    .matches(/^(?!0$)[0-9]+$/, {
      message: t('form.valid.maxDate', [0])
    })
    .max(2, t('form.valid.maxLength', [2]))
    .required(t('form.valid.required')),
  term: string().required(t('form.valid.fieldRequired')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') })),
  reportType: string().required(t('form.valid.fieldRequired'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any /*paramentPayrollStandard*/>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: process } = useField<string>(ref('process'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: paymentDate } = useField<string[] | undefined>(
  ref('paymentDate')
)
const { value: concepts } = useField<string>(ref('concepts'))
const { value: banks } = useField<string[]>(ref('banks'))
const { value: currency } = useField<string>(ref('currency'))
const { value: paymentBases } = useField<string[]>(ref('paymentBases'))
const { value: paymentMethod } = useField<string>(ref('paymentMethod'))
const { value: paymentReference } = useField<string>(ref('paymentReference'))
const { value: description } = useField<string>(ref('description'))
const { value: reference1 } = useField<string>(ref('reference1'))
const { value: reference2 } = useField<string>(ref('reference2'))
const { value: reference3 } = useField<string>(ref('reference3'))
const { value: reference4 } = useField<string>(ref('reference4'))
const { value: secuence } = useField<string>(ref('secuence'))
const { value: term } = useField<string>(ref('term'))
const { value: employees } = useField<any[]>(ref('employees'))
const { value: reportType } = useField<string>(ref('reportType'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  values.concepts = allIdsConceptsSelected.value
  const createReport: ReportBanamexD = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateBanamexD(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

const redirectRecordType = () => {
  if (reportType.value == "1") {
    getListProcessedPayroll()
  } else if (reportType.value == "2"){
    getListLoansPension()
  }
  getTermsByPaymentType()
}

/* Listar nóminas procesadas */
const getListProcessedPayroll = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  itemsEmployees.value = []
  employees.value = []
  try {
    if (
      payrolls.value?.length > 0 &&
      process.value != undefined &&
      startDate.value != undefined &&
      endDate.value != undefined
    ) {
      loading.value = true
      messageLoading.value = t('paymentReceipts.loading.payroll')
      let strPayrolls = await convertArrayToString(payrolls.value)
      let _closedPayrollEmployees = await getClosedPayrolls(
        strPayrolls,
        `'${process.value}'`,
        startDate.value,
        endDate.value
      )
      let strEmployees = await convertArrayToString(_closedPayrollEmployees)
      getListEmployees(strEmployees)
      loading.value = false
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Lista de préstamos tipo pensión alimenticia */
const getListLoansPension = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  itemsEmployees.value = []
  employees.value = []
  try {
    if (
      payrolls.value?.length > 0 &&
      process.value != undefined &&
      startDate.value != undefined &&
      endDate.value != undefined
    ) {
      loading.value = true
      messageLoading.value = t('paymentReceipts.loading.payroll')
      let strPayrolls = await convertArrayToString(payrolls.value)
      let _closedPayrollEmployees = await getClosedPayrolls(
        strPayrolls,
        `'${process.value}'`,
        startDate.value,
        endDate.value
      )
      let loanTypes = await convertArrayToString(itemsLoansTypes.value)
      let employees = await convertArrayToString(_closedPayrollEmployees)
      if (employees !== '') {
        let _loanEmployees = await getLoans(
          strPayrolls,
          endDate.value,
          loanTypes,
          employees
        )
        let strEmployees = await convertArrayToString(_loanEmployees)
        employeesAgain.value = strEmployees
        getListEmployeesLoans(strEmployees)
      }

      loading.value = false
    }
  } catch (error: any) {
    showError(error)
  }
}

const getListEmployeesAgain = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  itemsEmployees.value = []
  employees.value = []
  try {
    if (
      payrolls.value?.length > 0 &&
      process.value != undefined &&
      startDate.value != undefined &&
      endDate.value != undefined &&
      banks.value?.length > 0
    ) {
      getListEmployees(employeesAgain.value)
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Listar empleados */
const getListEmployees = async (employeesIds: string) => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (employeesIds !== '') {
      let strBanks = await convertArrayToString(banks.value)
      itemsEmployees.value = await getEmployeesByAccountType(strBanks, employeesIds, paymentType)
      copyItemsEmployees.value = itemsEmployees.value
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar Plazos */
const getTermsByPaymentType = () => {
  try {
    paymentType = '002'
    if ( banks.value && banks.value.length === 1 && banks.value[0] === defaultBank.value && reportType.value == '1' ) {
      paymentType = '001'
      itemsTerms.value = getTerms(paymentType)
      term.value = '00'
    } else {
      itemsTerms.value = getTerms('002')
      term.value = itemsTerms.value.map((_: any) => _.id)[0]
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Listar empleados para tipo Reporte Pensión */
const getListEmployeesLoans = async (employeesIds: string) => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (employeesIds !== '') {
      itemsEmployees.value = await getEmployeesLoans(employeesIds)
      copyItemsEmployees.value = itemsEmployees.value
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar conceptos */
const listConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.concepts')
  itemsConcepts.value = []
  try {
    if (payrolls.value?.length > 0 && process.value != undefined) {
      const strPayroll = await convertArrayToString(payrolls.value)
      allConcepts.value = await getConcepts(
        strPayroll,
        'banamex-d',
        '',
        `'${process.value}'`
      )
      itemsConcepts.value = [
        ...new Map(
          allConcepts.value.map((item: any) => [item['code'], item])
        ).values()
      ]
      let netConcept = itemsConcepts.value.filter(_ =>
        _.code.includes('999999')
      )[0]?.code
      concepts.value = netConcept
      filterCodeConcepts(netConcept)
      if (itemsConcepts.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Filtrar codigos de conceptos */
const filterCodeConcepts = async (concepts: any) => {
  allIdsConceptsSelected.value = allConcepts.value
  allIdsConceptsSelected.value = allIdsConceptsSelected.value
    .filter(item => item.code === concepts)
    .map(_ => _.id)
}

/* Filtrar bancos */
const filterBanks = () => {
  if (reportType.value == "1") {
    // getListProcessedPayroll()
    let isEqual =
      beforeBanks.value.sort().toString() === banks.value.sort().toString()
    if (isEqual) return
    let employeesBanks: any = []
    employeesBanks = copyItemsEmployees.value
    employeesBanks = employeesBanks.filter(function (employee: any) {
      var employeeBanks = employee.bankId.split(', ')
      return employeeBanks.some(function (bank: any) {
        return banks.value.includes(bank)
      })
    })
    employees.value = employeesBanks.map((_: any) => _.id)
    itemsEmployees.value = employeesBanks
    beforeBanks.value = banks.value
  } 
  getTermsByPaymentType()
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [
      promisePayrolls,
      promiseProcess,
      promiseBanks,
      promiseCurrencies,
      promisePaymentBases,
      promisePaymentMethod,
      promiseBankParentId,
      promiseGetLoansTypes
    ] = await Promise.all([
      getPayroll(store.user?.id as string),
      getProcessCountry(),
      getBankId(decodeCode),
      getCurrenciesId(decodeCode),
      getPaymentBases(),
      getPaymentMethodByBank(storePayroll.payroll?.company.id),
      getBankParentId(decodeCode),
      getLoansTypes("'PENSION-ALIMENTICIA-PR', 'PENSION-ALIMENTICIA-MO'"),
    ])
    itemsLoansTypes.value = promiseGetLoansTypes.map((_: any) => _.id)

    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsProcesses.value = promiseProcess
    itemsBanks.value = promiseBanks
    banks.value = [promiseBankParentId]
    defaultBank.value = promiseBankParentId
    itemsCurrencies.value = promiseCurrencies
    currency.value = itemsCurrencies.value.map((_: any) => _.id)[0]
    itemsPaymentBases.value = promisePaymentBases
    paymentBases.value = itemsPaymentBases.value.map((_: any) => _.id)
    itemsTerms.value = getTerms('001')
    itemsPaymentMethod.value = promisePaymentMethod
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.banamex-d')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="getListProcessedPayroll(), listConcepts()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    type="text"
                    :label="t('reports.fields.fileNature')"
                    v-model="reportType"
                    :options="itemsReportTypes"
                    :error="errors.reportType"
                    :required="true" 
                    @change="redirectRecordType" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.process')"
                    v-model="process"
                    :required="true"
                    :options="filterOptions(itemsProcesses, searchProcesses)"
                    :error="errors.process"
                    @filter="searchProcesses = $event"
                    @change="listConcepts(), getListProcessedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="redirectRecordType" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="redirectRecordType" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2" v-show="!reportTypeValidation">
                  <DSelectFieldSearch
                    :label="t('reports.fields.concept')"
                    v-model="concepts"
                    :required="!reportTypeValidation"
                    :disabled="reportTypeValidation"
                    :value-key="'code'"
                    :options="filterOptions(itemsConcepts, searchConcept)"
                    :error="errors.concepts"
                    @filter="searchConcept = $event"
                    @change="filterCodeConcepts(concepts)" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2" v-show="!reportTypeValidation">
                  <DMultiselect
                    :label="t('reports.fields.banks')"
                    v-model="banks"
                    :required="!reportTypeValidation"
                    :disabled="reportTypeValidation"
                    :header-title="t('reports.fields.banks')"
                    :options="itemsBanks"
                    :error="errors.banks"
                    @change="filterBanks" />
                </div>
                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('reports.tabs.fileData')" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.generationCurrency')"
                    v-model="currency"
                    :required="true"
                    :options="filterOptions(itemsCurrencies, searchCurrencies)"
                    @filter="searchCurrencies = $event"
                    :error="errors.currency"
                    :disabled="true" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2" v-show="!reportTypeValidation">
                  <DMultiselect
                    :label="t('reports.fields.paymentBases')"
                    :required="!reportTypeValidation"
                    :disabled="reportTypeValidation"
                    :header-title="t('reports.fields.paymentBases')"
                    v-model="paymentBases"
                    :options="itemsPaymentBases"
                    :error="errors.paymentBases" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.paymentDate')"
                    v-model="paymentDate"
                    :required="true"
                    :error="errors.paymentDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.paymentMethod')"
                    v-model="paymentMethod"
                    :required="true"
                    :options="filterOptions(itemsPaymentMethod, searchPaymentMethod)"
                    :error="errors.paymentMethod"
                    @filter="searchPaymentMethod = $event" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.reference')"
                    v-model="paymentReference"
                    :error="errors.paymentReference"
                    :required="true" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.secuence')"
                    v-model="secuence"
                    :error="errors.secuence"
                    :required="true" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.term')"
                    v-model="term"
                    :options="itemsTerms"
                    :error="errors.term"
                    :required="isRequiredField" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.description')"
                    v-model="description"
                    :error="errors.description" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.reference') + ' 1'"
                    v-model="reference1"
                    :error="errors.reference1" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.reference') + ' 2'"
                    v-model="reference2"
                    :error="errors.reference2" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.reference') + ' 3'"
                    v-model="reference3"
                    :error="errors.reference3" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.reference') + ' 4'"
                    v-model="reference4"
                    :error="errors.reference4" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.generateFile')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
